import { useCallback } from 'react'
import {
  safePtrAccess,
  useComparison,
  usePRFile,
  useZoomToFitNode,
  useZoomToFitRect,
} from '@sketch-hq/sketch-web-renderer'

const MAX_COVERAGE = 0.6

interface RevealTarget {
  uuid?: string
  layerPathHash?: string
  /** Where to go for a deleted layer, which has no node. */
  entityUUID?: string | null
}

/**
 * Fits the camera, down only, to a change picked from the list: its node, or
 * where the renderer draws it from the other version, or the frame a deleted
 * layer was in.
 */
export function useRevealChange() {
  const prFile = usePRFile()
  const zoomToFitNode = useZoomToFitNode()
  const zoomToFitRect = useZoomToFitRect()
  // Read at click time: every row of the list has one of these
  const { getBounds } = useComparison()

  return useCallback(
    (target: RevealTarget) => {
      // The renderer may have deleted it since it was read; using it would throw
      const file = safePtrAccess(prFile)

      if (!file) return

      const own = target.layerPathHash
        ? file.findByLayerPathHash(target.layerPathHash)
        : target.uuid
          ? file.findByUUID(target.uuid)
          : null

      if (!own && target.uuid) {
        const drawn = getBounds().get(target.uuid)

        if (drawn) {
          zoomToFitRect(drawn, MAX_COVERAGE, true)
          return
        }
      }

      const node =
        own ?? (target.entityUUID ? file.findByUUID(target.entityUUID) : null)

      const identifier = node?.getIdentifier()
      if (identifier === undefined) return

      zoomToFitNode(identifier, MAX_COVERAGE, true)
    },
    [prFile, zoomToFitNode, zoomToFitRect, getBounds]
  )
}
